import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import {
  Plus, Search, GraduationCap, MoreHorizontal, UserX, UserCheck, Archive
} from './components/DeskeyIcons';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Skeleton } from './components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { notifyError } from './lib/errors';
import { confirmDialog } from './components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStudents() {
  const { user, token } = useApp();
  const [students, setStudents] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', password: '' });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    if (!token) return;
    const tid = user?.tenant_id;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [su, sp, cl] = await Promise.all([
        axios.get(`${API}/users`, { params: { tenant_id: tid, role: 'student' }, headers }),
        axios.get(`${API}/student-profiles`, { params: { tenant_id: tid }, headers }),
        axios.get(`${API}/classes`, { params: { tenant_id: tid }, headers }),
      ]);
      setStudents(su.data);
      setProfiles(sp.data);
      setClasses(cl.data);
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (token) fetchData(); }, [user?.tenant_id, token]);

  const createStudent = async () => {
    if (!form.email || !form.full_name) { toast.error('All fields required'); return; }
    setCreating(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/users`, { ...form, role: 'student', tenant_id: user.tenant_id }, { headers });
      toast.success('Student created');
      setCreateOpen(false);
      setForm({ email: '', full_name: '', password: '' });
      fetchData();
    } catch (e) {
      notifyError(e, 'Failed to create student');
    } finally { setCreating(false); }
  };

  const filtered = students.filter(s =>
    s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = students.filter(s => s.status === 'active').length;
  const profileMap = Object.fromEntries(profiles.map(p => [p.user_id, p]));
  const billableCount = profiles.filter(p => p.billing_status === 'billable' && p.student_status === 'active').length;

  return (
    <DashboardShell title="Students" breadcrumb="Admin Dashboard">
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
            <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-button" style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
                <Plus className="w-4 h-4 mr-2" /> Add Student
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl">
              <DialogHeader><DialogTitle>Add New Student</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Full Name</Label>
                  <Input placeholder="Student Name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Email</Label>
                  <Input type="email" placeholder="student@school.edu" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Initial Password</Label>
                  <Input type="password" minLength={8} required value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </div>
                <Button onClick={createStudent} disabled={creating} className="w-full" style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
                  {creating ? 'Creating...' : 'Create Student'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Students', value: students.length, color: 'hsl(var(--chart-1))' },
            { label: 'Active', value: activeCount, color: 'hsl(var(--success))' },
            { label: 'Billable', value: billableCount, color: 'hsl(var(--warning))' },
            { label: 'Classes', value: classes.length, color: 'hsl(var(--chart-2))' },
          ].map(s => (
            <div key={s.label} className="kpi-card">
              <p className="kpi-label">{s.label}</p>
              <p className="kpi-number" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <GraduationCap className="empty-state-icon" />
            <p className="text-sm font-medium">No students found</p>
          </div>
        ) : (
          <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full desky-table">
                  <thead>
                    <tr>
                      <th className="text-left">Student</th>
                      <th className="text-left hidden sm:table-cell">Email</th>
                      <th className="text-left hidden md:table-cell">Class</th>
                      <th className="text-left">Status</th>
                      <th className="text-left hidden lg:table-cell">Billing</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(s => {
                      const profile = profileMap[s.id];
                      const cls = classes.find(c => c.id === profile?.class_id);
                      return (
                        <tr key={s.id}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback style={{ background: 'hsl(var(--chart-2) / 0.1)', color: 'hsl(var(--chart-2))', fontSize: '0.75rem' }}>
                                  {s.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm font-medium">{s.full_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 hidden sm:table-cell">
                            <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{s.email}</span>
                          </td>
                          <td className="py-3 px-4 hidden md:table-cell">
                            <span className="text-sm">{cls?.name || '—'}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${s.status}`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 hidden lg:table-cell">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              profile?.billing_status === 'billable' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {profile?.billing_status || 'billable'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="text-red-600" onClick={async () => {
                                  if (!(await confirmDialog({ title: 'Deactivate student', message: 'Deactivate this student? They will no longer be able to sign in.', danger: true, confirmLabel: 'Deactivate' }))) return;
                                  const headers = { Authorization: `Bearer ${token}` };
                                  await axios.delete(`${API}/users/${s.id}`, { headers });
                                  toast.success('Student deactivated');
                                  fetchData();
                                }}>
                                  <UserX className="w-4 h-4 mr-2" /> Deactivate
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardShell>
  , { headers });
}
